import { api } from './axios'
import { authService } from './auth'
import type { User, Setor } from '../models'

export interface CreateUserRequest {
  name: string
  email: string
  funcao_usuario: 'USUARIO' | 'DIRETOR' | 'ADMIN'
  cargo_usuario: string
  setor_logado_fk?: number | undefined
}

export interface UpdateUserRequest {
  name?: string | undefined
  email?: string | undefined
  funcao_usuario?: 'USUARIO' | 'DIRETOR' | 'ADMIN' | undefined
  cargo_usuario?: string | undefined
  setor_logado_fk?: number | undefined
}

export interface UserFilters {
  buscar?: string
  setor?: number | string
  funcao?: string
}

export const usuarioService = {
  /**
   * Lista todos os usuários com filtros opcionais
   */
  async getAll(filters?: UserFilters): Promise<User[]> {
    await authService.ensureSanctumInitialized()
    const params = new URLSearchParams()
    if (filters?.buscar) params.append('buscar', filters.buscar)
    if (filters?.setor && filters.setor !== '' && filters.setor !== 'todos') {
      params.append('setor', filters.setor.toString())
    }
    if (filters?.funcao && filters.funcao !== 'ALL' && filters.funcao !== 'todas') {
      params.append('funcao', filters.funcao)
    }
    const queryString = params.toString() ? `?${params.toString()}` : ''
    const response = await api.get<User[]>(`/usuarios${queryString}`)
    return response.data
  },

  /**
   * Busca um usuário específico por ID
   */
  async getById(id: number): Promise<User> {
    await authService.ensureSanctumInitialized()
    const response = await api.get<User>(`/usuarios/${id}`)
    return response.data
  },

  /**
   * Lista usuários de um setor específico
   */
  async getBySetor(setorId: number): Promise<User[]> {
    await authService.ensureSanctumInitialized()
    const response = await api.get<User[]>(`/usuarios/setor/${setorId}`)
    return response.data
  },

  /**
   * Cria um novo usuário
   */
  async create(data: CreateUserRequest): Promise<User> {
    await authService.ensureSanctumInitialized()
    const response = await api.post<User>('/usuarios', data)
    return response.data
  },

  /**
   * Atualiza um usuário existente
   */
  async update(id: number, data: UpdateUserRequest): Promise<User> {
    await authService.ensureSanctumInitialized()
    const response = await api.put<User>(`/usuarios/${id}`, data)
    return response.data
  },

  /**
   * Desativa um usuário (soft delete)
   */
  async delete(id: number): Promise<User> {
    await authService.ensureSanctumInitialized()
    const response = await api.delete<User>(`/usuarios/${id}`)
    return response.data
  },

  /**
   * Alterna a preferência de recebimento de emails do usuário logado
   */
  async toggleEmailPreference(): Promise<{ message: string; emails: boolean }> {
    await authService.ensureSanctumInitialized()
    const response = await api.post<{ message: string; emails: boolean }>('/usuario/receber-emails')
    return response.data
  },

  /**
   * Vincula múltiplos usuários a um setor
   */
  async vincularUsuarios(setorId: number, usuariosIds: number[]): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized()
    const response = await api.post<{ message: string }>(`/usuarios/${setorId}/setores/vincular`, {
      usuarios_ids: usuariosIds
    })
    return response.data
  },

  /**
   * Desvincula um usuário de um setor
   */
  async desvincularUsuario(usuarioId: number, setorId: number): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized()
    const response = await api.delete<{ message: string }>(`/usuarios/${usuarioId}/setores/${setorId}`)
    return response.data
  },

  /**
   * Retorna todos os setores do usuário logado
   */
  async getMeusSetores(): Promise<Setor[]> {
    await authService.ensureSanctumInitialized()
    const response = await api.get<Setor[]>('/me/setores')
    return response.data
  },

  /**
   * Define o setor logado do usuário
   */
  async definirSetorLogado(setorId: number): Promise<{ message: string; setor_logado: Setor; user: User }> {
    await authService.ensureSanctumInitialized()
    const response = await api.put<{ message: string; setor_logado: Setor; user: User }>(
      `/me/setor-logado/${setorId}`
    )
    return response.data
  }
}

